<!--
  RecommendedProductsContainer.vue

  This component can be used for compare products for all devices.
-->

<template>
  <section id="compare-products">
    <div class="top-content">
      <section class="wizard">
        <div class="mobile-dropdown">
          <div class="mobile-dropdown__label">
            <label>Current Step</label>
            <p>Step 2 of 2</p>
          </div>
          <div class="mobile-dropdown__display" @click="toggleDropdown()">
            <p>My recommendations</p>
            <svg
              :class="{
                'mobile-dropdown-show': mobileDropdownShow,
              }"
            >
              <use xlink:href="/assets/public/images/icons.svg#down-arrow" />
            </svg>
          </div>
        </div>
        <div class="step-indicator fr-view" :class="{ 'mobile-dropdown-show': mobileDropdownShow }">
          <ul class="desktop">
            <li class="step-four-progress">
              <button class="non-button-style" @click.prevent="updateStep('MyDetails')" aria-label="Change my details">Change my details</button>
            </li>
            <li class="active final-step-indicator" v-if="!isTableOpen">
              <button class="prev non-button-style" @click.prevent="updateStep('MyRecommendation')" aria-label="My recommendations">My recommendations</button>
            </li>
          </ul>
        </div>
      </section>
      <section class="price-factor" v-if="!isTableOpen">
        <h2 class="h4">My recommendations</h2>
        <PriceFactorStatement v-if="needAnalysisData !== null" :data="needAnalysisData" :isChangeCover="true">
          <template v-slot:title>
            <p id="price-factor-title">Here's what you told us</p>
          </template>
        </PriceFactorStatement>
        <div v-if="!isApiFail && !isApiLoading && !noRecommendationsFound" class="recommendation-content" v-html="MY_RECOMMENDATIONS_INFORMATION_TEXT"></div>
      </section>
    </div>

    <div class="top">
      <div v-if="!isTableOpen" :class="[isErrorMessageShow ? 'error-message-panel' : null]" class="products">
        <Loading class="centre-loading" v-if="isApiLoading"></Loading>
        <div v-if="(isErrorMessageShow && !isApiLoading) || isApiFail" class="error-message">
          <div>
            <svg>
              <use :xlink:href="'/assets/public/images/icons.svg#error'" />
            </svg>
            <div>
              <p>Sorry, we couldn’t find any product recommendations.</p>
              <p>
                Go back to
                <a @click.prevent="updateStep('MyDetails')" aria-label="change my details">Change my details</a>
                .
              </p>
            </div>
          </div>
        </div>
        <div v-if="!noRecommendationsFound" class="product-cards-container">
          <template v-for="(recommendation, i) in fullRecommendationList" :key="`product-${i}`">
            <RecommendedProductCard
              v-if="!isErrorMessageShow && !isApiFail && !isApiLoading"
              :allHeightsCaptured="allHeightsCaptured"
              :isCurrentCover="maxQuoteNum - 1 === i"
              :paymentFrequency="paymentFrequency"
              :productCardIndex="i"
              :productGuideInfo="getUniqueProducts(fullRecommendationList, i, recommendation.recommendationName)"
              :productPromotionalTag="promotionalTags[i]"
              :recommendation="recommendation"
              :selectedExcessValue="excessValues[i]"
              :sectionHeights="sectionHeights"
              :accordionStates="accordionStates"
              :isMemberPortal="true"
              @excessUpdated="updateChosenExcessValues"
              @changeCover="updateChosenProduct"
              @sectionHeightUpdated="updateSectionHeights"
              @update:accordionState="(state) => (accordionStates = state)"
              @emailQuote="saveQuoteDetailsAndSendEmail"
            />
          </template>
        </div>
      </div>
    </div>
    <EmailQuoteModal
      :modalActive="isEmailModalVisible"
      :apiStatus="apiStatus.quoteEmail"
      :emailSentTo="emailSentTo"
      @emailQuoteHook="emailQuoteHook"
      @closeModal="isEmailModalVisible = false"
    />
  </section>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import PriceFactorStatement from '@general/js/components/PriceFactorStatement.vue';
import { getUniqueProductGuides } from '@general/js/utils';
import Loading from '@general/js/components/loading.vue';
import { RecommendedProductCard } from '@/components';
import { ProductType } from '@general/js/enums';
import EmailQuoteModal from '@/views/MyCover/ChangeCover/ChangeProduct/EmailQuoteModal.vue';
import ChangeCoverMixins from '../ChangeCoverMixins';

export default {
  name: 'RecommendedProductsContainer',
  mixins: [ChangeCoverMixins],
  components: {
    Loading,
    PriceFactorStatement,
    RecommendedProductCard,
    EmailQuoteModal,
  },

  props: {
    refData: {
      type: Object,
      required: true,
    },

    isApiFail: {
      type: Boolean,
      default: false,
    },

    isApiLoading: {
      type: Boolean,
      default: false,
    },

    needAnalysisData: {
      type: Object,
      required: true,
    },

    fullRecommendationList: {
      type: Array,
      required: true,
    },

    hospitalNeeds: {
      type: Array,
      required: true,
    },

    extrasNeeds: {
      type: Array,
      required: true,
    },

    flexiFeatures: {
      type: Array,
    },

    maxQuoteNum: {
      type: Number,
      default: 3,
    },

    promotionalTags: {
      type: Array,
    },

    isErrorMessageShow: {
      type: Boolean,
      default: false,
    },

    excessValues: {
      type: Array,
    },
  },

  data() {
    return {
      allHeightsCaptured: false,
      isTableOpen: false,
      mobileDropdownShow: false,
      sectionHeights: {
        discountsAndLoadingsMaxHeight: 0,
        headingMaxHeight: 0,
        productDescriptionsMaxHeight: 0,
      },
      ProductType,
      accordionStates: { hospital: {}, extras: {} },
      isEmailModalVisible: false,
      emailSentTo: null,
      apiStatus: {
        quoteEmail: {
          loading: false,
          fail: false,
          success: false,
          isInvalidEmail: false,
        },
      },
    };
  },

  methods: {
    updateStep(step) {
      this.$parent.updateStep(step);
    },

    saveQuoteDetailsAndSendEmail(selectedQuote, selectedExcess) {
      this.updateSelectedProducts(selectedQuote);
      this.updateSelectedExcess(selectedExcess);

      this.isEmailModalVisible = true;

      if (!this.apiStatus.quoteEmail.isInvalidEmail) {
        this.emailQuoteHook();
      }
    },

    getUniqueProducts(recommendationList, index, combinedName) {
      let recommendations = cloneDeep(recommendationList);
      recommendations = recommendations.map((recommendation) => {
        const newProducts = recommendation.products.map((product) => {
          return {
            ...product,
            guideUrl: product.productGuideLink,
            name: product.productName,
          };
        });
        return {
          ...recommendation,
          products: newProducts,
        };
      });

      return getUniqueProductGuides(recommendations[index].products, combinedName);
    },

    toggleDropdown(isShow) {
      if (isShow) this.mobileDropdownShow = isShow;
      else this.mobileDropdownShow = !this.mobileDropdownShow;
    },

    updateChosenProduct(data) {
      // Save selected products and excess into vuex
      this.updateSelectedProducts(data.selectedProducts);
      this.updateSelectedExcess(data.selectedExcess);

      this.redirectToQAPage();
    },

    updateChosenExcessValues(excessValue, excessIndex) {
      if (this.excessValues[excessIndex] !== excessValue) {
        this.excessValues[excessIndex] = excessValue;
        this.$emit('excessValuesUpdated', this.excessValues);
      }
    },

    updateSectionHeights(newSectionHeights) {
      if (!this.allHeightsCaptured) {
        this.sectionHeights.headingMaxHeight = Math.max(this.sectionHeights.headingMaxHeight, newSectionHeights.headingHeight);
        this.sectionHeights.productDescriptionsMaxHeight = Math.max(this.sectionHeights.productDescriptionsMaxHeight, newSectionHeights.productDescriptionHeight);
        this.sectionHeights.discountsAndLoadingsMaxHeight = Math.max(this.sectionHeights.discountsAndLoadingsMaxHeight, newSectionHeights.discountsAndLoadingsHeight);
      }
    },

    jumpToField(step) {
      this.$parent.jumpToField(step);
    },

    getUniqueProductGuides,
  },

  computed: {
    noRecommendationsFound() {
      // For when there is only one item in fullRecommendationList (only has current cover and no recommendations)
      return this.fullRecommendationList && this.fullRecommendationList.length <= 1;
    },
  },

  watch: {
    // Watch the maxHeight object for changes
    sectionHeights: {
      deep: true,
      handler() {
        // Once all product cards have mounted and emitted their heights, set allHeightsCaptured to true
        if (
          Object.values(this.sectionHeights).every((value) => {
            return value > 0;
          })
        ) {
          this.allHeightsCaptured = true;
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@general/styles/_responsive_mixins.scss';
@import '@general/styles/_constants.scss';
@import '@general/styles/_mixins.scss';
@import '@general/styles/components/_compareProductsTable.scss';

.top-content {
  @include grid-layout;
  padding: 3rem 0;

  @include tablet-and-above {
    @include grid-template(1fr 3fr);
  }

  @include mobile {
    @include grid-template(1fr);
  }
}

:deep(.wizard) {
  & > .step-indicator {
    @include flex-row;
    background-color: $WHITE;
    width: inherit;

    @include mobile {
      display: none;

      &.mobile-dropdown-show {
        display: block;
      }
    }

    ul {
      @include flex-col;
      width: 100%;
    }
  }
}

#compare-products {
  padding: 0;
  background: none;
  flex-direction: column;
  position: relative;

  & > div:not(.top-content) {
    @include flex-row;

    @include mobile {
      @include flex-col;
    }

    & > div:not(.step-indicator):not(.quote-heads) {
      z-index: 1;
      display: flex;
      flex: 1;

      @include tablet-and-above {
        padding: 0 hutter(0.5) 3rem;
      }

      &.products {
        display: block;
      }
    }
  }

  h2 {
    margin-bottom: 2rem;
  }
}

.top {
  div.error-message div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3.75rem 0;
    margin: 0;

    svg {
      width: 1.75rem;
      height: 1.75rem;
      padding-bottom: 1.875rem;
    }
  }
}

a,
.non-button-style * {
  font-weight: $BOLD;
}

.bottom {
  padding-bottom: 120px;

  div {
    display: flex;
    flex-direction: column;
    font-size: font-size(-1);

    a + a {
      margin-top: 0.5em;
    }
  }

  &:after {
    content: '';
    display: block;
    width: calc(1 / var(--gridColumns) * 80%);
    position: absolute;
    background: $GREY_100;
    background-size: 5px 100%;
    right: 0;
    bottom: 0;
    top: -2em;
  }
}

.product-cards-container {
  @include flex-row;
  padding: 0;

  & > div:not(.recommendation-card) {
    display: flex;
    flex: 1;
    padding: 1em hutter(0.5);
    z-index: 1;
  }

  @include mobile {
    @include flex-col;
  }
}

// Mobile View Styling
.mobile-dropdown {
  display: none;

  @include mobile {
    display: block;
  }

  &__label {
    @include flex-row;
    justify-content: space-between;
    margin-bottom: 0.5rem;

    label {
      font-weight: $BOLD;
    }

    p {
      margin: 0;
      color: $GREY_600;
    }
  }

  &__display {
    @include flex-row;
    padding: $MD;
    border: 1.5px solid $PRIMARY_GREY_40;
    background-color: $PRIMARY_BRIGHT_GREEN_20;
    border-left: 2px solid $PRIMARY_MEDIUM_GREEN_100;
    font-weight: $FONT_WEIGHT_STRONG;
    cursor: pointer;
    column-gap: $MD;
    align-items: center;

    &::before {
      content: '';
      width: 24px;
      height: 24px;
      border-radius: $ROUND;
      background: url('/assets/public/images/icon_progress_stepper_active.svg') center/24px 24px no-repeat;
    }

    p {
      margin: 0;
      line-height: 1.5rem;
    }

    svg:last-child {
      pointer-events: none;
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(-50%, -50%);
      width: 1.5rem;
      height: 1.5rem;
      color: $GREY_600;

      &.mobile-dropdown-show {
        transform: translate(-50%, -50%) rotate(180deg);
      }
    }
  }
}
</style>
